import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import type EventStream from 'Models/EventStream';
import {
  phraseInProgressSnapshot,
  playTimeNoteFlushController,
  type RepertoirePlaySettings,
} from 'Models/PlayTimeNoteFlushController';
import { PhaserToReactListeners, RemoveListener } from 'Phaser/EventBus2';
import type { SectionPerfectedArgs } from 'Phaser/EventBus2/ToReact';
import { repertoireSectionPerfectedEventAction } from 'Actions/events';
import type { ErrorRecognitionActive } from 'Types';
import type { AppDispatch } from 'Reducers';

// Repertoire play-time capture: the notes of a piece are recorded as they are
// played (loop passes and phrases stopped partway included), through the same
// flush controller sight reading uses, with the practice settings in force on
// every batch. A batch is cut on a 2s timer, at a phrase boundary, on pause,
// before a loop clears its notes for the next pass, before any setting changes,
// on unmount and on page teardown (the App's hooks), so each batch carries the
// settings its notes were played under.

type RepertoirePlayTimeCaptureOptions = {
  eventStream: Pick<EventStream, 'extractUnflushedRepertoireBatches' | 'getPhraseInProgress'>;
  playSessionId: string | null | undefined;
  repertoireId: number | undefined;
  // False in listen mode, which grades nothing.
  capturing: boolean;
  errorRecognitionMode: string;
  repertoirePlaying: boolean;
  loopActive: boolean;
  errorRecognitionActive: ErrorRecognitionActive;
  transposeSemitones: number;
  modeConversion: string;
  // Null when the Perfect Phrase counter is off.
  perfectPhraseTarget: number | null;
};

type LoopBounds = { startTimestamp: number; endTimestamp: number } | null;

export const handsGradedFor = (
  errorRecognitionActive: ErrorRecognitionActive,
): RepertoirePlaySettings['handsGraded'] => {
  if (errorRecognitionActive.left && errorRecognitionActive.right) return 'HT';
  if (errorRecognitionActive.left) return 'LH';
  if (errorRecognitionActive.right) return 'RH';
  return null;
};

const settingsFor = (
  options: RepertoirePlayTimeCaptureOptions,
  loopBounds: LoopBounds,
): RepertoirePlaySettings => ({
  loopActive: options.loopActive,
  loopStartTick: options.loopActive && loopBounds ? loopBounds.startTimestamp : null,
  loopEndTick: options.loopActive && loopBounds ? loopBounds.endTimestamp : null,
  handsGraded: handsGradedFor(options.errorRecognitionActive),
  transposeSemitones: options.transposeSemitones,
  modeConversion: options.modeConversion,
  perfectPhraseTarget: options.perfectPhraseTarget,
});

const sameSettings = (a: RepertoirePlaySettings, b: RepertoirePlaySettings): boolean =>
  a.loopActive === b.loopActive &&
  a.loopStartTick === b.loopStartTick &&
  a.loopEndTick === b.loopEndTick &&
  a.handsGraded === b.handsGraded &&
  a.transposeSemitones === b.transposeSemitones &&
  a.modeConversion === b.modeConversion &&
  a.perfectPhraseTarget === b.perfectPhraseTarget;

export function useRepertoirePlayTimeCapture(options: RepertoirePlayTimeCaptureOptions): void {
  const dispatch = useDispatch<AppDispatch>();
  const {
    eventStream,
    playSessionId,
    repertoireId,
    capturing,
    errorRecognitionMode,
    repertoirePlaying,
  } = options;

  const loopBoundsRef = useRef<LoopBounds>(null);
  // The latest options, for the effects and handlers below.
  const optionsRef = useRef(options);
  optionsRef.current = options;
  // The settings the batches cut now are stamped with. Updated only after the
  // notes played under the previous settings have been cut.
  const settingsRef = useRef<RepertoirePlaySettings>(settingsFor(options, null));
  const errorRecognitionModeRef = useRef(errorRecognitionMode);
  errorRecognitionModeRef.current = errorRecognitionMode;
  const sessionRef = useRef({ playSessionId, repertoireId });
  sessionRef.current = { playSessionId, repertoireId };

  // One capture per practice session of a piece; stopping sends what is left.
  useEffect(() => {
    if (!capturing || !playSessionId || repertoireId === undefined) return undefined;
    playTimeNoteFlushController.configure(
      (_lookback, includeHeld) => eventStream.extractUnflushedRepertoireBatches(includeHeld),
      {
        surface: 'repertoire',
        playSessionId,
        repertoireId,
        errorRecognitionMode: errorRecognitionModeRef.current,
        getSettings: () => settingsRef.current,
      },
      phraseInProgressSnapshot(eventStream),
    );
    return () => {
      playTimeNoteFlushController.stop().catch(() => {});
    };
  }, [capturing, playSessionId, repertoireId, eventStream]);

  useEffect(() => {
    playTimeNoteFlushController.setErrorRecognitionMode(errorRecognitionMode);
  }, [errorRecognitionMode]);

  // A setting changed: cut what was played under the old one, then take it. Read
  // after the render, so a loop range the scene reported meanwhile is included.
  useEffect(() => {
    const nextSettings = settingsFor(optionsRef.current, loopBoundsRef.current);
    if (sameSettings(settingsRef.current, nextSettings)) return;
    playTimeNoteFlushController.flush('settings').catch(() => {});
    settingsRef.current = nextSettings;
  });

  useEffect(() => {
    if (repertoirePlaying) return;
    playTimeNoteFlushController.flush('pause').catch(() => {});
  }, [repertoirePlaying]);

  useEffect(() => {
    // A new loop range applies from the next pass; the pass played over the old
    // range was cut when the loop reset (LoopPassEnding comes first).
    // The scene reports the range when a loop is turned on, before the settings
    // effect has taken the loop as on; that effect then reads it from the ref.
    const onLoopBounds = (bounds: LoopBounds) => {
      loopBoundsRef.current = bounds;
      const loopActive = settingsRef.current.loopActive;
      settingsRef.current = {
        ...settingsRef.current,
        loopStartTick: loopActive && bounds ? bounds.startTimestamp : null,
        loopEndTick: loopActive && bounds ? bounds.endTimestamp : null,
      };
    };
    const onLoopPassEnding = () => {
      playTimeNoteFlushController.flush('pass-end').catch(() => {});
    };
    const onPhraseCompleted = () => {
      playTimeNoteFlushController.flush('boundary').catch(() => {});
    };
    const onSectionPerfected = (section: SectionPerfectedArgs) => {
      const settings = settingsRef.current;
      const { playSessionId: sessionId, repertoireId: pieceId } = sessionRef.current;
      if (!sessionId || pieceId === undefined || !settings.handsGraded) return;
      dispatch(
        repertoireSectionPerfectedEventAction({
          repertoire_id: pieceId,
          play_session_id: sessionId,
          loop_start_tick: section.loopStartTimestamp,
          loop_end_tick: section.loopEndTimestamp,
          loop_start_measure: section.loopStartMeasure,
          loop_end_measure: section.loopEndMeasure,
          perfect_phrase_target: section.streakTarget,
          bpm: section.bpm,
          hands_graded: settings.handsGraded,
          transpose_semitones: settings.transposeSemitones,
          mode_conversion: settings.modeConversion,
        }),
      );
    };
    PhaserToReactListeners.SetLoopBounds(onLoopBounds);
    PhaserToReactListeners.LoopPassEnding(onLoopPassEnding);
    PhaserToReactListeners.RepertoirePhraseCompleted(onPhraseCompleted);
    PhaserToReactListeners.SectionPerfected(onSectionPerfected);
    return () => {
      RemoveListener('SetLoopBounds', onLoopBounds);
      RemoveListener('LoopPassEnding', onLoopPassEnding);
      RemoveListener('RepertoirePhraseCompleted', onPhraseCompleted);
      RemoveListener('SectionPerfected', onSectionPerfected);
    };
    // Registered once; the handlers read the latest values through refs.
  }, [dispatch]);
}
